import { isAdjustable, toKey } from './permissions';
import {
  LEVEL_ADJUST,
  SYSTEM_ROLE_PERMS,
  normalizeLevel,
  normalizeRole,
  type Level,
  type Role,
} from './roles';

export type OverrideMode = 'grant' | 'deny';

export interface PermissionOverride {
  permission: string;
  mode: OverrideMode;
}

export interface BranchScope {
  branchIds: string[];
  /** True when the user is not restricted to specific branches. */
  allBranches: boolean;
}

export interface EffectivePermissions {
  permissions: ReadonlySet<string>;
  scopes: BranchScope;
  has(key: string): boolean;
}

export interface ComputeEffectiveInput {
  /** Permission key lists, one per assigned role. Unioned. */
  rolePermSets?: readonly (readonly string[])[];
  /** Fallback when the user has no rows in `rbac_user_roles`. */
  fallbackRole?: string | null;
  fallbackLevel?: string | null;
  /**
   * The adjustable keys the fallback role holds in this clinic's database —
   * what the clinic chose on it in Setări → Roluri, taken off or added. The
   * fallback reads the role's definition from this package, not from the
   * database, so without this the choice would reach only the people
   * explicitly assigned to the role. Null: the role has no row yet, keep the
   * definition.
   */
  fallbackAdjustable?: readonly string[] | null;
  overrides?: readonly PermissionOverride[];
  scopes?: BranchScope | null;
}

/**
 * Resolve a user's effective permissions.
 *
 * Order of application, and it matters:
 *   1. union of every assigned role's permissions
 *   2. seniority adjustment for the staff role on the person's record
 *   3. per-user overrides, where deny beats grant
 *
 * Deny winning is what makes an override usable as a revocation: an admin can
 * strip one capability from someone without rebuilding their role.
 */
export function computeEffective(input: ComputeEffectiveInput = {}): EffectivePermissions {
  const {
    rolePermSets = [],
    overrides = [],
    scopes = null,
    fallbackRole,
    fallbackLevel,
    fallbackAdjustable,
  } = input;

  const set = new Set<string>();

  if (rolePermSets.length > 0) {
    for (const arr of rolePermSets) {
      for (const k of arr ?? []) set.add(toKey(k));
    }
    /*
     * The junior denials survive an explicit role.
     *
     * They used to apply only on the fallback path, so ticking "Medic" for a
     * junior doctor in the roles editor — the obvious thing to do, and what
     * the editor invites — handed back exactly what the level was there to
     * withhold: approving a treatment plan and signing a radiology report.
     * Seniority is a fact about the person, not about the role they hold, so
     * it is read from their record whichever way their permissions arrive.
     *
     * Only the denials. The level's grants widen a system role, and an
     * explicitly assigned role says precisely what the clinic chose to give;
     * adding to it would be the same surprise in the other direction. A
     * clinic that wants a junior to approve says so with a per-person grant,
     * which step 3 still honours.
     */
    if (fallbackRole != null) {
      const adj = LEVEL_ADJUST[normalizeRole(fallbackRole)]?.[normalizeLevel(fallbackLevel)];
      for (const k of adj?.deny ?? []) set.delete(k);
    }
  } else if (fallbackRole != null) {
    // No explicit role assignment: fall back to the system role on the staff
    // row. Without this a freshly created user would have no permissions at
    // all until someone opened the roles screen.
    const role: Role = normalizeRole(fallbackRole);
    for (const k of SYSTEM_ROLE_PERMS[role] ?? []) set.add(k);
    if (fallbackAdjustable != null) {
      for (const k of [...set]) if (isAdjustable(k)) set.delete(k);
      for (const k of fallbackAdjustable) if (isAdjustable(k)) set.add(k);
    }

    const level: Level = normalizeLevel(fallbackLevel);
    const adj = LEVEL_ADJUST[role]?.[level];
    if (adj) {
      for (const k of adj.grant ?? []) set.add(k);
      for (const k of adj.deny ?? []) set.delete(k);
    }
  }

  const grants = new Set<string>();
  const denies = new Set<string>();
  for (const o of overrides) {
    const k = toKey(o.permission);
    if (o.mode === 'grant') grants.add(k);
    else if (o.mode === 'deny') denies.add(k);
  }
  for (const k of grants) set.add(k);
  for (const k of denies) set.delete(k); // deny beats grant

  const frozen: ReadonlySet<string> = set;
  return {
    permissions: frozen,
    scopes: scopes ?? { branchIds: [], allBranches: true },
    has: (k: string) => frozen.has(toKey(k)),
  };
}

export function hasPerm(effective: EffectivePermissions | null | undefined, key: string): boolean {
  if (!effective) return false;
  return effective.has(key);
}

/** Does this user's branch scope include the given branch? */
export function canAccessBranch(
  scopes: BranchScope | null | undefined,
  branchId: string | null,
): boolean {
  if (!scopes || scopes.allBranches) return true;
  if (branchId == null) return true;
  return scopes.branchIds.map(String).includes(String(branchId));
}

export interface FinanceLockConfig {
  /** Nothing dated before this may be edited. */
  lockBeforeDate?: string | null;
  /** Nothing older than this many days may be edited. */
  editWindowDays?: number | string | null;
}

/**
 * Financial period locking: can a record with this date still be edited?
 *
 * Used to stop retroactive edits after a month has been reported. `bypass` is
 * for the one role allowed to reopen a closed period.
 */
export function isFinanceEditable(
  recordDate: string | Date | null | undefined,
  cfg: FinanceLockConfig = {},
  bypass = false,
): boolean {
  if (bypass) return true;
  if (!recordDate) return true;

  const d = new Date(recordDate);
  if (Number.isNaN(d.getTime())) return true;

  if (cfg.lockBeforeDate) {
    const lb = new Date(cfg.lockBeforeDate);
    if (!Number.isNaN(lb.getTime()) && d < lb) return false;
  }

  if (cfg.editWindowDays != null && cfg.editWindowDays !== '') {
    const days = Number(cfg.editWindowDays);
    if (!Number.isNaN(days) && days >= 0) {
      const elapsedDays = (Date.now() - d.getTime()) / 86_400_000;
      if (elapsedDays > days) return false;
    }
  }

  return true;
}
